import {
  createContext,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useEffect,
} from 'react'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { type Product } from '@/types/product'

import { useSKUListShortcut } from './SKUListShortcut'

interface SkuListContextValue {
  product: Product
  isExpanded: boolean
  shouldShowAction: boolean
  onToggle: (event: MouseEvent<HTMLButtonElement>) => void
}

export const SkuListContext = createContext<SkuListContextValue>(
  {} as SkuListContextValue
)

type SkuListProviderProps = PropsWithChildren<
  Pick<SkuListContextValue, 'product'>
>

export function SkuListProvider(props: SkuListProviderProps) {
  const { product, children } = props
  const [isExpanded, setIsExpanded] = useLocalStorage(
    `skusExpandedForProduct${product.id}`,
    false
  )
  const {
    subscribeToBatchExpand,
    unsubscribeToBatchExpand,
    onClickToExpandSKUItem,
  } = useSKUListShortcut()

  const onToggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      onClickToExpandSKUItem(event, product.id, isExpanded)
    },
    [isExpanded, onClickToExpandSKUItem, product.id]
  )

  useEffect(() => {
    subscribeToBatchExpand(product.id, setIsExpanded)

    return () => {
      unsubscribeToBatchExpand(product.id)
    }
  }, [
    product.id,
    setIsExpanded,
    subscribeToBatchExpand,
    unsubscribeToBatchExpand,
  ])

  return (
    <SkuListContext.Provider
      value={{
        product,
        isExpanded,
        shouldShowAction: product.totalSku > 0,
        onToggle,
      }}
    >
      {children}
    </SkuListContext.Provider>
  )
}
